import { Box, Center, Flex, Image, Spinner } from '@chakra-ui/react'
import { ReactElement, useMemo, useRef } from 'react'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { checkOperationPermission, PermissionsEnum } from '../../../../utils'
import { ACLPermissionEnum } from '../../../constants'
import type { FileDetailsResponse, MediaLibrarySearchFileResponse } from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import PermissionGuard from '../../PermissionGuard'
import { ThreadAvatarOverlay } from '../Details/Threads'
import LazyNewThreadPanel from '../Details/Threads/LazyNewThreadPanel'
import IconPreview from './IconPreview'
import type { FilePreviewProps } from '.'

const ImagePreview = ({
  file,
  folderPath,
  transformation = [],
  showThreads = false,
  disablePadding,
  extension,
  height,
  width,
  showPreview = false,
}: FilePreviewProps): ReactElement => {
  const [{ showNewThreadPanel, threads = [] }, dispatch] = useContext(MLContext)
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { thumbnail, url } = file
  const previewUrl = (file as FileDetailsResponse).previewUrl
  const [showOverlay, setShowOverlay] = useState(true)
  const [repaintOverlay, setRepaintOverlay] = useState(true)
  const [dismissOverlay, setDismissOverlay] = useState(localStorage.getItem('dismissOverlay') === 'true')
  const imageRef = useRef<HTMLImageElement>(null)

  const { t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(true)
  const { user } = useContext(UserContext)

  const imageURLToUse = useMemo(() => {
    if (showPreview) {
      return previewUrl ?? url
    }
    return thumbnail
  }, [previewUrl, thumbnail, url, showPreview])

  const [threadPanelPosition, setThreadPanelPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const handleImageClick = (
    event: React.MouseEvent<HTMLImageElement>,
  ): void => {
    if (!showThreads) {
      return
    }
    const imageContainer = event.currentTarget
    const rect = imageContainer.getBoundingClientRect()

    const x = ((event.clientX - rect.left) / imageContainer.offsetWidth) * 100
    const y = ((event.clientY - rect.top) / imageContainer.offsetHeight) * 100

    dispatch({
      type: MediaLibraryActions.SET_SELECTED_THREAD,
      payload: null,
    })
    setShowOverlay(false)
    setRepaintOverlay(false)

    if (!dismissOverlay) {
      setDismissOverlay(true)
      localStorage.setItem('dismissOverlay', 'true')
    }

    setThreadPanelPosition({ x, y })
    dispatch({
      type: MediaLibraryActions.SHOW_NEW_THREAD_PANEL,
      payload: true,
    })
  }

  useEffect(() => {
    if (!showNewThreadPanel) {
      setThreadPanelPosition(null)
    }
  }, [showNewThreadPanel])

  useEffect(() => {
    if (threads.length > 0) {
      setRepaintOverlay(false)
    }

    if (showNewThreadPanel) {
      setShowOverlay(false)
      setRepaintOverlay(false)

      if (!dismissOverlay) {
        setDismissOverlay(true)
        localStorage.setItem('dismissOverlay', 'true')
      }
    }

    if (repaintOverlay && threads.length === 0 && !showNewThreadPanel) {
      setShowOverlay(true)
    }
  }, [threads, showNewThreadPanel, repaintOverlay, dismissOverlay])

  return (
    <Box position="relative" height="100%" display="inline-flex">
      <Image
        justifySelf="center"
        alignSelf="center"
        objectFit="contain"
        cursor={showThreads ? 'pointer' : 'default'}
        ref={imageRef}
        maxHeight="100%"
        {...(extension === 'svg' ? { height, width } : {})}
        src={imageURLToUse}
        onClick={handleImageClick}
        onLoad={() => {
          setLoading(false)
        }}
        onError={() => {
          setRepaintOverlay(false)
          setLoading(false)
        }}
        fallback={
          loading ? (
            <Center>
              <Spinner />
            </Center>
          ) : (
            <IconPreview
              file={file}
              folderPath={folderPath}
              transformation={transformation}
              disablePadding={disablePadding}
            />
          )
        }
      />
      {showThreads && !isPublicLink ? (
        <>
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission &&
              checkOperationPermission({
                user,
                aclPermission: [
                  ACLPermissionEnum.CONTRIBUTE,
                  ACLPermissionEnum.MANAGE,
                ],
                selectedFiles: [file] as MediaLibrarySearchFileResponse[],
                selectedFolders: [],
                selectedMediaCollections: [],
              }) ? (
                <>
                  <LazyNewThreadPanel
                    fileDetails={file}
                    position={threadPanelPosition}
                    setThreadPanelPosition={setThreadPanelPosition}
                  />
                  {Number(imageRef.current?.offsetWidth) > 120 &&
                    !loading &&
                    !dismissOverlay &&
                    repaintOverlay &&
                    showOverlay && (
                      <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        alignItems="center"
                        justifyContent="center"
                        bg="rgba(0, 0, 0, 0.5)"
                        pointerEvents="none"
                        color="white"
                        fontWeight="600"
                        fontSize="18px"
                      >
                        <Box textAlign="center">
                          {t(
                            'click_anywhere_on_the_picture_to_leave_a_comment',
                            'Click anywhere on the picture to leave a comment',
                          )}
                        </Box>
                      </Flex>
                    )}
                </>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
          <ThreadAvatarOverlay fileDetails={file} />
        </>
      ) : null}
    </Box>
  )
}

export default ImagePreview
